import React, { useEffect, useState, useRef, useMemo } from "react";
import _ from "lodash";
import { Title, Text, Chip, Loader } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  findVariationOption,
  getSelectedVariationValue,
  getVariationOptionValue,
  getVisibleVariationOptions,
  updateProductVariations,
} from "../helpers/orderProductVariationUtils";
import {
  getSelectedCustomValue,
  updateProductCustomOptions,
  filterCustomFieldsBySelections,
  pruneInvalidCustomSelections,
} from "../helpers/commissionCustomConditionUtils";
import { useServerApi } from "../hooks/userServerApi";

const VariationSelect = ({ variation, productVariations, onSelect }) => {
  const options = getVisibleVariationOptions(variation.options);
  const selectedValue = getSelectedVariationValue(
    productVariations,
    variation.name
  );

  return (
    <>
      <Title order={6} mb={"sm"}>
        {variation.name}
      </Title>
      <Chip.Group
        value={selectedValue}
        onChange={(value) => onSelect(variation, value)}
        multiple={false}
        mb={"xl"}
      >
        {options.length > 0 &&
          options.map((option) => {
            const optionValue = getVariationOptionValue(option);
            return (
              <Chip key={optionValue} value={optionValue}>
                {option.label}
              </Chip>
            );
          })}
      </Chip.Group>
    </>
  );
};

const CustomCommissionFieldSelect = ({
  field,
  productCustomOptions,
  onSelect,
}) => {
  const selectedValue = getSelectedCustomValue(productCustomOptions, field.name);

  return (
    <>
      <Title order={6} mb={"sm"}>
        {field.name}
      </Title>
      <Chip.Group
        value={selectedValue}
        onChange={(value) => {
          const option = field.options.find((o) => o.value === value);
          onSelect(field, option || null);
        }}
        multiple={false}
        mb={"xl"}
      >
        {field.options.map((option) => (
          <Chip key={option.condition || option.value} value={option.value}>
            {option.label}
          </Chip>
        ))}
      </Chip.Group>
    </>
  );
};

const OrderProductVariation = ({ form: mainForm }) => {
  const product = _.get(mainForm.values, "productSnapShot");
  const productVariations = mainForm.values.productVariations || [];
  const productCustomOptions = mainForm.values.productCustomOptions || [];
  const signedDate = mainForm.values.signedDate;
  const professionalInvestor = mainForm.values.professionalInvestor ?? false;
  const { t } = useTranslation();
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [customFields, setCustomFields] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [loadingCustomFields, setLoadingCustomFields] = useState(false);
  const [customFieldsError, setCustomFieldsError] = useState(null);

  const signedDateKey = signedDate
    ? new Date(signedDate).toISOString().slice(0, 10)
    : null;

  useEffect(() => {
    const productId = product?._id;
    if (!productId || !signedDateKey) {
      setCustomFields([]);
      setCustomRows([]);
      setCustomFieldsError(null);
      setLoadingCustomFields(false);
      mainForm.setFieldValue("commissionCustomFieldsMeta", []);
      return;
    }

    let cancelled = false;

    const fetchCustomFields = async () => {
      setLoadingCustomFields(true);
      setCustomFieldsError(null);
      try {
        const result =
          await apiRef.current.Product.getCustomCommissionConditions(
            productId,
            {
              signedDate: signedDateKey,
              professionalInvestor,
            }
          );
        if (!cancelled) {
          setCustomFields(result?.fields || []);
          setCustomRows(result?.rows || []);
          mainForm.setFieldValue(
            "commissionCustomFieldsMeta",
            (result?.fields || []).map((field) => ({ name: field.name }))
          );
        }
      } catch (error) {
        if (!cancelled) {
          setCustomFields([]);
          setCustomRows([]);
          setCustomFieldsError(error?.message || "Failed to load custom fields");
        }
      } finally {
        if (!cancelled) {
          setLoadingCustomFields(false);
        }
      }
    };

    fetchCustomFields();

    return () => {
      cancelled = true;
    };
  }, [product?._id, signedDateKey, professionalInvestor]);

  useEffect(() => {
    if (!customFields.length || !customRows.length) return;

    const current = mainForm.values.productCustomOptions || [];
    const pruned = pruneInvalidCustomSelections(
      customFields,
      customRows,
      current
    );
    if (JSON.stringify(pruned) !== JSON.stringify(current)) {
      mainForm.setFieldValue("productCustomOptions", pruned);
    }
  }, [customFields, customRows]);

  const handleVariationChange = (variation, selectedValue) => {
    const selectedOption = findVariationOption(
      variation.options,
      selectedValue
    );
    if (!selectedOption) return;

    mainForm.markUserEdited?.();
    mainForm.setFieldValue(
      "productVariations",
      updateProductVariations(
        mainForm.values.productVariations,
        variation,
        selectedOption
      )
    );
  };

  const visibleCustomFields = useMemo(
    () =>
      filterCustomFieldsBySelections(
        customFields,
        customRows,
        productCustomOptions
      ),
    [customFields, customRows, productCustomOptions]
  );

  const handleCustomFieldChange = (field, selectedOption) => {
    mainForm.markUserEdited?.();
    let nextOptions = updateProductCustomOptions(
      mainForm.values.productCustomOptions,
      field.name,
      selectedOption
    );
    nextOptions = pruneInvalidCustomSelections(
      customFields,
      customRows,
      nextOptions
    );
    mainForm.setFieldValue("productCustomOptions", nextOptions);
  };

  const hasProductVariations = !_.isEmpty(product?.variations);
  const hasCustomFields = visibleCustomFields.length > 0;
  const showEmptyState =
    !hasProductVariations && !hasCustomFields && !loadingCustomFields;

  return (
    <>
      {!signedDateKey && (
        <Text size="xs" color="dimmed" mb="md">
          {t("Please complete Sign Date on the Sign Detail step first.")}
        </Text>
      )}

      {loadingCustomFields && <Loader size="xs" mb="md" />}

      {customFieldsError && (
        <Text size="xs" color="red" mb="md">
          {customFieldsError}
        </Text>
      )}

      {showEmptyState && (
        <Text size={"xs"}>
          {t("There are no variations for this product.")}
        </Text>
      )}

      {product?.variations?.map((variation) => (
        <VariationSelect
          key={variation._id || variation.name}
          variation={variation}
          productVariations={productVariations}
          onSelect={handleVariationChange}
        />
      ))}

      {visibleCustomFields.map((field) => (
        <CustomCommissionFieldSelect
          key={field.name}
          field={field}
          productCustomOptions={productCustomOptions}
          onSelect={handleCustomFieldChange}
        />
      ))}
    </>
  );
};

export default OrderProductVariation;
